// confirmDialog.ts — a small, reusable confirm/guard modal for destructive or irreversible actions
// (e.g. "Quit this match?", "Reset progress?"). Built on the shared modal() primitive: a hazard
// warning disc, a title + message, and a two-button row (CANCEL secondary + CONFIRM). When `danger`
// is set the top-rail + confirm button turn hazard-red so the destructive choice is unmistakable.
// Closing via the ✕ or the veil counts as CANCEL. Pure UI; no Core/network. (Tactical OLED redesign.)
import Phaser from 'phaser';
import { PALETTE, hex, FONT } from './keys';
import { modal } from './ui';
import { ctaButton, secondaryButton } from './ui/kit';
import { scaledFont } from './settings';

export interface ConfirmDialogOpts {
  title: string;
  message: string;
  /** Label for the confirm button (e.g. "QUIT", "RESET"). Defaults to "CONFIRM". */
  confirmLabel?: string;
  /** Label for the cancel button. Defaults to "CANCEL". */
  cancelLabel?: string;
  /** Destructive styling: hazard-red top-rail + confirm button, with a red warning mark. */
  danger?: boolean;
  onConfirm: () => void;
  onCancel?: () => void;
}

/** Open a small confirm dialog. Returns the Modal handle. Dismissing (✕/veil) calls onCancel. */
export function confirmDialog(scene: Phaser.Scene, opts: ConfirmDialogOpts): ReturnType<typeof modal> {
  const W = scene.scale.width;
  const H = scene.scale.height;
  const danger = !!opts.danger;
  // Destructive signature: a solid hazard-red top-rail (vs the calm scan rail for an info confirm).
  const accent = danger ? PALETTE.hazard : PALETTE.scan;
  // The affirmative CTA stays green for a non-destructive confirm; hazard-red when destructive.
  const ctaFill = danger ? PALETTE.hazard : PALETTE.correct;
  let confirmed = false;

  // Mirror modal()'s centered-card width so wrap widths + button widths line up exactly.
  const widthFrac = 0.82;
  const cardW = Math.min(W * widthFrac, 440);

  // — measure the title + message first so the card hugs its content (no dead vertical space) —
  const title = scene.add
    .text(0, 0, opts.title, {
      fontFamily: FONT.display,
      fontSize: `${scaledFont(20, 15)}px`,
      color: hex(PALETTE.text),
      fontStyle: 'bold',
      align: 'center',
      wordWrap: { width: cardW - 48 },
    })
    .setOrigin(0.5, 0);
  const msg = scene.add
    .text(0, 0, opts.message, {
      fontFamily: FONT.family,
      fontSize: `${scaledFont(14, 12)}px`,
      color: hex(PALETTE.dim),
      align: 'center',
      lineSpacing: 4,
      wordWrap: { width: cardW - 52 },
    })
    .setOrigin(0.5, 0);

  // — top-anchored layout (0 = card top); back-solve a compact card height —
  const discR = 28; // 56px warning disc
  const padTop = 58; // clears the rail/divider band
  const circleCenterT = padTop + discR; // disc centre, from card top
  const titleTopT = circleCenterT + discR + 22; // disc bottom + gap
  const msgTopT = titleTopT + title.height + 10;
  const btnH = 50;
  const btnTopT = msgTopT + msg.height + 28;
  const cardHTarget = btnTopT + btnH + 30; // + bottom padding

  const m = modal(scene, {
    title: '', // empty header keeps the disc + title centred in the body
    widthFrac,
    heightFrac: cardHTarget / H,
    railGrad: [accent],
    onClose: () => {
      if (!confirmed) opts.onCancel?.();
    },
  });
  const { card, cardW: realCardW, cardH } = m;
  const top = -cardH / 2; // from-top → card-centre offset

  // — warning disc: accent-tinted fill + ring, holding a triangle-with-! (danger) or i-dot (info) —
  const markY = top + circleCenterT;
  const disc = scene.add.graphics();
  disc.fillStyle(accent, 0.15);
  disc.fillCircle(0, markY, discR);
  disc.lineStyle(2, accent, 0.4);
  disc.strokeCircle(0, markY, discR);
  card.add(disc);
  card.add(warningGlyph(scene, markY, accent, danger));

  // — title (bold, centred) + message (dim, wrapped, centred) —
  title.setY(top + titleTopT);
  msg.setY(top + msgTopT);
  card.add([title, msg]);

  // — button row: CANCEL (secondary) left + CONFIRM (cta; hazard when danger) right —
  const btnCenterY = top + btnTopT + btnH / 2;
  const sideM = 22;
  const gap = 12;
  const halfW = (realCardW - sideM * 2 - gap) / 2;
  card.add(
    secondaryButton(scene, -realCardW / 2 + sideM + halfW / 2, btnCenterY, opts.cancelLabel ?? 'Cancel', () => m.close(), {
      width: halfW,
      height: btnH,
      fontPx: 16,
    }),
  );
  card.add(
    ctaButton(
      scene,
      realCardW / 2 - sideM - halfW / 2,
      btnCenterY,
      opts.confirmLabel ?? 'Confirm',
      () => {
        confirmed = true;
        m.close();
        opts.onConfirm();
      },
      {
        width: halfW,
        height: btnH,
        fill: ctaFill,
        glow: danger ? null : ctaFill,
        ink: PALETTE.ink,
        fontPx: 16,
      },
    ),
  );

  return m;
}

/** Draw the warning mark centred at (0, cy): a hazard triangle with a punched-out "!" for danger,
 *  an i-dot otherwise. The "!" is cut in `ink` so it reads as a dark notch on the bright triangle. */
function warningGlyph(scene: Phaser.Scene, cy: number, color: number, danger: boolean): Phaser.GameObjects.Graphics {
  const g = scene.add.graphics();
  if (danger) {
    const s = 30;
    // Up-pointing triangle in the accent (hazard) color.
    g.fillStyle(color, 1);
    g.fillTriangle(0, cy - s * 0.44, s * 0.52, cy + s * 0.36, -s * 0.52, cy + s * 0.36);
    // Punch the exclamation out in ink so it reads as a dark cut-out (matches the reference).
    g.fillStyle(PALETTE.ink, 1);
    g.fillRect(-s * 0.055, cy - s * 0.18, s * 0.11, s * 0.3);
    g.fillCircle(0, cy + s * 0.26, s * 0.075);
  } else {
    g.fillStyle(color, 1);
    g.fillCircle(0, cy - 7, 3.2);
    g.fillRoundedRect(-3, cy - 1, 6, 13, 2);
  }
  return g;
}
